/**
 * Agent-directory API — `GET /api/v1/agents`.
 *
 * Wire shape (Hub's `AgentBriefOut` schema, `hub/app/api/schemas.py`):
 *
 *   [
 *     { "id": "<uuid>", "display_name": "Alice Alpha", "role": "agent" },
 *     { "id": "<uuid>", "display_name": "Bob Beta",   "role": "admin" },
 *     ...
 *   ]
 *
 * The Hub guarantees:
 *   - Exactly three keys per item: `id`, `display_name`, `role`.
 *   - `email` and `password_hash` are NEVER present (CTO directive).
 *   - Items are ordered alphabetically by `display_name`.
 *   - The list is flat (not paged): ~5–20 agents total for this product.
 *
 * Consumers should go through `useAgentsById` rather than calling
 * `listAgents` directly — the hook fetches once per session and caches.
 */

import { z } from 'zod';

import { apiClient } from '@/lib/api-client';

/** Zod schema for one agent directory entry. */
export const agentBriefSchema = z.object({
  id: z.string().uuid(),
  display_name: z.string(),
  role: z.enum(['agent', 'admin']),
});

export type AgentBrief = z.infer<typeof agentBriefSchema>;

/** `GET /api/v1/agents` — returns the full agent directory. */
export async function listAgents(): Promise<AgentBrief[]> {
  const response = await apiClient.get('/agents');
  return z.array(agentBriefSchema).parse(response.data);
}

// Admin agent management ----------------------------------------------------

/**
 * Zod schema for one admin agent row — mirrors the Hub's `AgentAdminOut`
 * (`hub/app/api/schemas.py`): the public directory fields plus `email`, the
 * `disabled` flag and a live `online` flag (at least one fresh WebSocket
 * connection). `password_hash` is still never present. `online` defaults to
 * `false` on the wire only from `GET /agents/admin`; other responses (e.g. the
 * PATCH disable/enable row) may omit it, so we `.default(false)` to be safe.
 */
export const agentAdminSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  display_name: z.string(),
  role: z.enum(['agent', 'admin']),
  disabled: z.boolean(),
  online: z.boolean().default(false),
});

export type AgentAdmin = z.infer<typeof agentAdminSchema>;

/**
 * `GET /api/v1/agents/admin` — admin-only agent list with management fields.
 *
 * Non-admin callers get a 403 from the Hub's `require_admin` dependency; the
 * console never issues this call for them (the query is gated on the dialog
 * being open, and the dialog is admin-only).
 */
export async function listAgentsAdmin(): Promise<AgentAdmin[]> {
  const response = await apiClient.get('/agents/admin');
  return z.array(agentAdminSchema).parse(response.data);
}

/**
 * `PATCH /api/v1/agents/{id}` — set an agent's `disabled` flag (admin only).
 *
 * `disabled=true` bars the agent from logging in AND force-logs-out their
 * existing session; `disabled=false` restores access. Returns the updated
 * row on 200. The Hub rejects self-disable and admin-disable with a 403
 * (`detail: "cannot disable your own account"` / `"cannot disable an admin
 * account"`) and unknown ids with a 404 (`detail: "agent not found"`).
 */
export async function setAgentDisabled(
  agentId: string,
  disabled: boolean,
): Promise<AgentAdmin> {
  const response = await apiClient.patch(`/agents/${agentId}`, { disabled });
  return agentAdminSchema.parse(response.data);
}

/** Minimum password length — mirrors the Hub's `MIN_PASSWORD_LENGTH` (8).
 *  A floor, not a strength policy; the Hub enforces the same as a 422. */
export const MIN_PASSWORD_LENGTH = 8;

/** Body of `POST /api/v1/agents` — create a new console agent. */
export interface CreateAgentInput {
  email: string;
  display_name: string;
  role: 'agent' | 'admin';
  password: string;
}

/**
 * `POST /api/v1/agents` — create a console agent (admin only). Returns the new
 * admin row on 201. The Hub answers `409` with detail "an agent with this
 * email already exists" on a duplicate email and `422` on a malformed email /
 * bad role / short password — both surface via the mutation's error toast.
 */
export async function createAgent(input: CreateAgentInput): Promise<AgentAdmin> {
  const response = await apiClient.post('/agents', input);
  return agentAdminSchema.parse(response.data);
}

/**
 * `DELETE /api/v1/agents/{id}` — hard-delete an agent that has no history
 * (admin only). Returns `204` (no body) on success. The Hub answers `409` when
 * the agent owns any attribution/audit rows (detail "agent has … and cannot be
 * deleted; disable it instead"), `403` for self / admin-role targets, and
 * `404` for an unknown id.
 */
export async function deleteAgent(agentId: string): Promise<void> {
  await apiClient.delete(`/agents/${agentId}`);
}

/**
 * `PATCH /api/v1/agents/{id}/password` — reset an agent's password (admin
 * only). Returns `204` (no body). The Hub rejects a password shorter than
 * {@link MIN_PASSWORD_LENGTH} with a `422` and an unknown id with a `404`.
 */
export async function resetAgentPassword(
  agentId: string,
  password: string,
): Promise<void> {
  await apiClient.patch(`/agents/${agentId}/password`, { password });
}

// Login events --------------------------------------------------------------

/**
 * Zod schema for one login-event row — mirrors the Hub's `LoginEventOut`
 * (`hub/app/api/schemas.py`): the resolved client `ip`, the `user_agent`
 * (nullable), and the `created_at` timestamp.
 */
export const loginEventSchema = z.object({
  id: z.string().uuid(),
  ip: z.string(),
  user_agent: z.string().nullable(),
  created_at: z.string(),
});

export type LoginEvent = z.infer<typeof loginEventSchema>;

/** Generic paged envelope — mirrors the Hub's `PagedResponse[T]`. */
export const loginEventsPageSchema = z.object({
  items: z.array(loginEventSchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
});

export type LoginEventsPage = z.infer<typeof loginEventsPageSchema>;

/**
 * `GET /api/v1/agents/{id}/login-events` — an agent's successful-login history,
 * newest first, paged (admin only). Default page size mirrors the Hub (50).
 */
export async function listAgentLoginEvents(
  agentId: string,
  params: { limit?: number; offset?: number } = {},
): Promise<LoginEventsPage> {
  const response = await apiClient.get(`/agents/${agentId}/login-events`, {
    params: { limit: params.limit ?? 50, offset: params.offset ?? 0 },
  });
  return loginEventsPageSchema.parse(response.data);
}
